import { useEffect, type ReactElement } from 'react';
import { ActionIcon, Badge, Box, Center, Loader, Menu, Stack, Text } from '@mantine/core';
import { Check, Flag as FlagIcon, MoreVertical, PanelRightOpen, RotateCcw, Search } from 'lucide-react';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import MegaGrid, { type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import StatBand, { type StatItem } from '@/design/StatBand';
import { GovernanceIssuesViewModel, type TrackedIssue } from './GovernanceIssuesViewModel';
import { useAuth } from '@/features/auth/AuthContext';
import { useGovernanceIssueStore } from './useGovernanceIssueStore';
import GovernanceIssueDetailDrawer from './GovernanceIssueDetailDrawer';
import type { IssueStatus } from './PolicyIssues';
import { formatWhen } from './PolicyFormatting';
import { ALL_STATUSES, KIND_META, SEVERITY_COLOR, SEVERITY_RANK, STATUS_META } from './IssueVisuals';

const STATUS_ICON: Record<IssueStatus, ReactElement> = {
    open: <RotateCcw size={14} />,
    investigating: <Search size={14} />,
    resolved: <Check size={14} />,
};

function StatusPill({ status }: { status: IssueStatus }): ReactElement {
    const meta = STATUS_META[status];
    return (
        <Box style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 999, background: meta.bg, border: `1px solid ${meta.border}`, color: meta.fg, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
            {meta.label}
        </Box>
    );
}

function statusCounts(rows: TrackedIssue[]): Record<IssueStatus, number> {
    return {
        open: rows.filter((i) => i.status === 'open').length,
        investigating: rows.filter((i) => i.status === 'investigating').length,
        resolved: rows.filter((i) => i.status === 'resolved').length,
    };
}

function statBand(counts: Record<IssueStatus, number>): StatItem[] {
    return [
        { label: 'Open', value: counts.open.toLocaleString('en-US') },
        { label: 'Investigating', value: counts.investigating.toLocaleString('en-US') },
        { label: 'Resolved', value: counts.resolved.toLocaleString('en-US') },
    ];
}

/**
 * The Issues tab: governance issues from both sources — policy verdicts and sensitivity-flag
 * alerts (PII / credentials / data exposure) — with a status workflow, flagging, assignment,
 * and a source/kind filter. Click a row to open the detail drawer (notes, audit, flagged turns).
 */
export default function GovernanceIssuesTab({ mineOnly = false }: { mineOnly?: boolean } = {}): ReactElement {
    const vm = useViewModel(GovernanceIssuesViewModel);
    useGovernanceIssueStore();
    const { user } = useAuth();
    const myEmail = user?.email ?? null;

    useEffect(() => {
        void vm.initialize();
    }, [vm]);

    if (!vm.loaded) {
        return (
            <Center py="xl">
                <Loader size="sm" />
            </Center>
        );
    }

    if (vm.errorMessage !== null && vm.activeIssue === null) {
        return (
            <Text c="red" size="sm">
                Couldn't load issues: {vm.errorMessage}
            </Text>
        );
    }

    function actionCell(issue: TrackedIssue): ReactElement {
        return (
            <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                    <ActionIcon variant="subtle" color="gray" aria-label="Issue actions" onClick={(e) => e.stopPropagation()}>
                        <MoreVertical size={16} />
                    </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                    <Menu.Item leftSection={<PanelRightOpen size={14} />} onClick={() => void vm.openIssue(issue)}>
                        Open details
                    </Menu.Item>
                    <Menu.Divider />
                    {ALL_STATUSES.filter((status) => status !== issue.status && status !== 'resolved').map((status) => (
                        <Menu.Item key={status} leftSection={STATUS_ICON[status]} onClick={() => void vm.setStatus(issue, status)}>
                            Mark as {STATUS_META[status].label}
                        </Menu.Item>
                    ))}
                    <Menu.Item leftSection={<FlagIcon size={14} />} onClick={() => void vm.setFlag(issue, !issue.flagged)}>
                        {issue.flagged ? 'Remove flag' : 'Flag for attention'}
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
        );
    }

    const columns: MegaGridColumn<TrackedIssue>[] = [
        {
            key: 'status',
            header: 'Status',
            sortValue: (i) => i.status,
            groupValue: (i) => STATUS_META[i.status].label,
            filter: { type: 'enum', accessor: (i) => i.status },
            render: (i) => <StatusPill status={i.status} />,
        },
        {
            key: 'severity',
            header: 'Severity',
            sortValue: (i) => SEVERITY_RANK[i.severity],
            groupValue: (i) => i.severity,
            filter: { type: 'enum', accessor: (i) => i.severity },
            render: (i) => (
                <Badge size="xs" radius="sm" variant="light" color={SEVERITY_COLOR[i.severity]}>
                    {i.severity}
                </Badge>
            ),
        },
        {
            key: 'source',
            header: 'Source',
            sortValue: (i) => KIND_META[i.kind].label,
            groupValue: (i) => KIND_META[i.kind].label,
            filter: { type: 'enum', accessor: (i) => KIND_META[i.kind].label },
            render: (i) => (
                <Badge size="xs" radius="sm" variant="light" color={KIND_META[i.kind].color}>
                    {KIND_META[i.kind].label}
                </Badge>
            ),
        },
        {
            key: 'title',
            header: 'Issue',
            width: 220,
            sortValue: (i) => i.title,
            groupValue: (i) => i.title,
            render: (i) => (
                <Text size="xs" fw={600} truncate>
                    {i.title}
                </Text>
            ),
        },
        {
            key: 'verdict',
            header: 'Verdict',
            width: 150,
            sortValue: (i) => i.verdict,
            filter: { type: 'enum', accessor: (i) => i.verdict },
            render: (i) => (
                <Text size="xs" truncate>
                    {i.verdict}
                </Text>
            ),
        },
        {
            key: 'person',
            header: 'Person',
            width: 160,
            sortValue: (i) => i.person,
            groupValue: (i) => i.department,
            render: (i) => (
                <Box>
                    <Text size="xs" truncate>
                        {i.person}
                    </Text>
                    <Text c="dimmed" style={{ fontSize: '9px' }} truncate>
                        {i.department}
                    </Text>
                </Box>
            ),
        },
        {
            key: 'when',
            header: 'When',
            width: 150,
            sortValue: (i) => new Date(i.timestampIso),
            render: (i) => (
                <Text size="xs" c="dimmed" ff="monospace">
                    {formatWhen(i.timestampIso)}
                </Text>
            ),
        },
        {
            key: 'flag',
            header: '',
            align: 'center',
            noGroup: true,
            render: (i) => (i.flagged ? <FlagIcon size={14} color="var(--bm-color-danger-text)" fill="var(--bm-color-danger-text)" /> : null),
        },
        {
            key: 'assignee',
            header: 'Assignee',
            width: 140,
            sortValue: (i) => i.assigneeUserId ?? '',
            render: (i) => (
                <Text size="xs" c={i.assigneeUserId ? undefined : 'dimmed'} truncate>
                    {i.assigneeUserId ?? 'Unassigned'}
                </Text>
            ),
        },
        {
            key: 'actions',
            header: '',
            align: 'right',
            hideable: false,
            noGroup: true,
            render: (i) => (
                <Box style={{ display: 'flex', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                    {actionCell(i)}
                </Box>
            ),
        },
    ];

    const rows = mineOnly ? vm.issues.filter((i) => myEmail !== null && i.assigneeUserId === myEmail) : vm.issues;
    const counts = mineOnly ? statusCounts(rows) : vm.counts;

    return (
        <Stack gap="md">
            <StatBand stats={statBand(counts)} />

            <MegaGrid
                columns={columns}
                rows={rows}
                getRowKey={(i) => i.key}
                onRowClick={(i) => void vm.openIssue(i)}
                persistenceKey={mineOnly ? 'governance-issues-mine-v1' : 'governance-issues-v5'}
                searchAccessor={(i) => `${i.title} ${i.verdict} ${i.person} ${i.department} ${i.severity} ${i.status} ${KIND_META[i.kind].label}`}
                searchPlaceholder="Search issues"
                striped
                density="compact"
                minWidth={640}
                clientPageSize={25}
                emptyMessage={mineOnly ? 'Nothing is assigned to you.' : 'No issues need attention — all clear.'}
            />

            <GovernanceIssueDetailDrawer vm={vm} />
        </Stack>
    );
}
